import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  refreshUserProfile,
} from "../../redux/slices/authSlice";
import { subscriptionData } from "../../redux/slices/subscriptionSlice";
import {
  subscriptionPayment,
  previewUpgrade,
  clearPreview,
} from "../../redux/slices/subscriptionPaymentSlice";

// ============================================================================
// Shared subscription upgrade UI.
//
// Single source of truth for BOTH:
//   • <PlanShield />        — the colored plan badge shown next to a user name;
//                             pass onClick to make it open the upgrade modal.
//   • <UpgradePlanModal />  — the upgrade sheet that offers only genuine
//                             upgrades from the current plan (both intervals),
//                             with the 3-day Pro-Yearly trial and Stripe checkout.
//
// Any page can import these and get the exact same UI/UX. Change it here once
// and every page updates.
// ============================================================================

// Subscription plan shield — same badge shown behind the author name on
// community posts (see PostCard.js). Colored by the current plan.
const PLAN_SHIELD_COLORS = {
  Free: "#B9C2CD",
  Plus: "#16A34A",
  Pro: "#0395D3",
  "Pro Trial": "#0395D3",
  "pro + newsletter": "#C8941C",
};

const PlanShield = ({ planName, onClick }) => {
  if (!planName) return null;
  const fill = PLAN_SHIELD_COLORS[planName] || PLAN_SHIELD_COLORS[planName.toLowerCase()];
  if (!fill) return null;
  const clickable = typeof onClick === "function";
  return (
    <span
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              onClick(e);
            }
          : undefined
      }
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      title={clickable ? "View upgrade options" : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginLeft: "4px",
        verticalAlign: "middle",
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24">
        <path
          d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z"
          fill={fill}
        />
        <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
        <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
      </svg>
    </span>
  );
};

// ============= Upgrade plan modal (community plan-badge tap) =============
// Opens when the user taps their plan shield. Shows only the plans that are a
// genuine UPGRADE from the current one, both billing intervals:
//   • Free  → Pro (monthly + yearly) + Pro + Newsletter (monthly + yearly)
//   • Pro   → Pro + Newsletter (monthly + yearly)
//   • Pro + Newsletter → "you're on the top plan" message
// Plan data + prices come from the subscriptionData slice; selecting a plan
// starts the same Stripe checkout the subscription page uses.
// Keyed by lowercased plan name so casing differences ("Pro + Newsletter" vs
// "pro + newsletter") don't break the upgrade-direction check.
const UPGRADE_PLAN_RANK = {
  free: 0,
  plus: 1,
  pro: 2,
  "pro trial": 2,
  "pro + newsletter": 3,
};

const upgradePlanRank = (name) =>
  UPGRADE_PLAN_RANK[String(name || "").trim().toLowerCase()] ?? 0;

// Pro Yearly comes with a 3-day free trial for members who haven't used it.
const PRO_TRIAL_DAYS = 3;
const isProYearlyOption = (option) =>
  !!option &&
  option.interval === "year" &&
  String(option.name || "").trim().toLowerCase() === "pro";

const UpgradePlanModal = ({
  show,
  onClose,
  // Telemetry tag for where the modal was opened from (checkout metadata +
  // trial route). Defaults to the community plan-badge entry point.
  source = "community_plan_badge",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const profileStore = useSelector((state) => state.profile);
  const plansRaw = useSelector(
    (state) => state.subscriptionData?.subscriptionDataItems,
  );
  // Proration preview for existing-subscription upgrades (Pro → Pro+Newsletter).
  const { previewData, isPreviewLoading, previewError } = useSelector(
    (state) => state.subscriptionPayment,
  );
  // Whether this member has already consumed their one-time Pro trial.
  const hasUsedProTrial =
    user?.has_used_pro_trial ??
    profileStore?.profileItem?.data?.has_used_pro_trial ??
    false;
  // A user who already has a paid (non-free, non-trial) plan is UPGRADING an
  // existing subscription — that path shows a proration confirmation step
  // (mirrors SubscriptionMembership.js's UpgradeConfirmationModal) instead of
  // going straight to a fresh Stripe Checkout.
  const hasExistingSubscription = Boolean(
    user?.plan &&
      user.plan.name &&
      user.plan.name !== "Free" &&
      user.plan.slug !== "free-trial",
  );
  // Yearly is the default cycle (best value). `selectedName` tracks the chosen
  // plan tier by name so it survives toggling the billing cycle.
  const [billingCycle, setBillingCycle] = useState("year");
  const [selectedName, setSelectedName] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  // "select" = plan picker · "confirm" = proration upgrade summary.
  const [view, setView] = useState("select");

  // Sliding pill for the billing toggle — mirrors SubscriptionMembership.js.
  // The slider animates via transform; the first placement is instant (no
  // transition) so it doesn't fly in from the left on open.
  const monthBtnRef = useRef(null);
  const yearBtnRef = useRef(null);
  const [sliderStyle, setSliderStyle] = useState({});
  const sliderHasPlaced = useRef(false);

  useLayoutEffect(() => {
    const activeBtn =
      billingCycle === "month" ? monthBtnRef.current : yearBtnRef.current;
    if (activeBtn) {
      setSliderStyle({
        width: activeBtn.offsetWidth + "px",
        transform: `translateX(${activeBtn.offsetLeft}px)`,
        transition: sliderHasPlaced.current
          ? "transform 260ms cubic-bezier(0.2,0.8,0.2,1), width 260ms cubic-bezier(0.2,0.8,0.2,1)"
          : "none",
      });
      sliderHasPlaced.current = true;
    }
    // Re-measure when the modal opens (refs mount) and when the cycle changes.
  }, [billingCycle, show]);

  // When the modal closes, reset the "placed" flag so it reopens without a
  // slide-in animation.
  useEffect(() => {
    if (!show) sliderHasPlaced.current = false;
  }, [show]);

  // Lock background page scroll while the modal is open; restore the previous
  // overflow value when it closes or unmounts.
  useEffect(() => {
    if (!show) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [show]);

  const plansList = Array.isArray(plansRaw) ? plansRaw : [];

  // Load plans lazily the first time the modal opens.
  useEffect(() => {
    if (show && plansList.length === 0) {
      dispatch(subscriptionData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Reset the selection each time the modal is dismissed so it reopens fresh.
  useEffect(() => {
    if (!show) {
      setSelectedName(null);
      setBillingCycle("year");
      setView("select");
      dispatch(clearPreview());
    }
  }, [show, dispatch]);

  const currentName = user?.plan?.name || "Free";
  const currentInterval = user?.plan?.interval || null;
  const currentRank = upgradePlanRank(currentName);

  // Eligible = any plan of a strictly higher tier than the current one, in both
  // intervals. Trial/free placeholder rows are dropped.
  const options = plansList
    .filter(
      (p) =>
        p &&
        typeof p.name === "string" &&
        p.name.trim() !== "" &&
        !["free", "free-trial", "pro-trial"].includes(p.slug) &&
        (p.interval === "month" || p.interval === "year"),
    )
    .filter((p) => upgradePlanRank(p.name) > currentRank)
    .map((p) => ({
      priceId: p.price_id,
      planId: p.id,
      name: p.name,
      interval: p.interval,
      price: p.price,
      discount: p.discount,
      rank: upgradePlanRank(p.name),
    }));

  // Group the flat option list into one card per plan tier, keeping both the
  // monthly and yearly variant so the billing toggle can swap between them.
  const tiers = [];
  options
    .slice()
    .sort((a, b) => a.rank - b.rank)
    .forEach((o) => {
      let tier = tiers.find((t) => t.name === o.name);
      if (!tier) {
        tier = { name: o.name, rank: o.rank, month: null, year: null };
        tiers.push(tier);
      }
      tier[o.interval] = o;
    });

  // Short feature blurb per tier (mirrors the subscription page copy).
  const TIER_BLURB = {
    Pro: "Unlimited access to every in-app feature.",
    "Pro + Newsletter": "Everything in Pro, plus the weekly Insider newsletter.",
  };

  // Externally-managed subscriptions (assigned by an admin, or purchased
  // through the mobile app / Google Play / App Store, or granted via referral)
  // can't be changed from the web — mirror SubscriptionMembership.js and show
  // an informational note instead of the plan picker.
  const subscriptionSource = user?.plan?.subscription_source;
  const isAdminUser = subscriptionSource === "admin";
  const isMobileUser = subscriptionSource === "mobile";
  const isGoogleUser = subscriptionSource === "google";
  const isAppleUser = subscriptionSource === "apple";
  const isReferralUser = subscriptionSource === "referral";
  const isUnmanagedSource =
    isAdminUser ||
    isMobileUser ||
    isGoogleUser ||
    isAppleUser ||
    isReferralUser;
  const sourceNote = isAdminUser
    ? "Your premium subscription is assigned by an administrator. Subscription upgrades or cancellations are not available here."
    : isMobileUser
    ? "Your subscription was purchased through the mobile app. Please manage your subscription through your mobile device."
    : isGoogleUser
    ? "Your subscription was purchased through Google Play. Please manage your subscription through the Google Play Store on your Android device."
    : isAppleUser
    ? "Your subscription was purchased through the App Store. Please manage your subscription through Settings on your Apple device."
    : isReferralUser
    ? "Your premium access was granted through a referral. Subscription upgrades or cancellations are not available for referral-based plans."
    : null;

  const plansLoading = plansList.length === 0;
  const atTopPlan = !plansLoading && tiers.length === 0;
  const optionFor = (tier) => (billingCycle === "year" ? tier.year : tier.month);
  const chosenTier = tiers.find((t) => t.name === selectedName) || null;
  const chosen = chosenTier ? optionFor(chosenTier) : null;

  // Offer the 3-day trial only on Pro Yearly, and only if it's still available.
  const trialAvailable = !hasUsedProTrial;
  const chosenIsTrial = trialAvailable && isProYearlyOption(chosen);

  // Best yearly saving across eligible tiers — shown on the billing toggle.
  const bestYearlyDiscount = tiers.reduce(
    (max, t) => Math.max(max, Number(t.year?.discount) || 0),
    0,
  );

  const currentLabel =
    currentName === "Free"
      ? "Free"
      : `${currentName}${
          currentInterval
            ? ` · ${currentInterval === "year" ? "Yearly" : "Monthly"}`
            : ""
        }`;

  // Runs the actual payment for the selected plan. Handles both paths:
  //   • new subscription  → backend returns a Stripe Checkout URL (redirect)
  //   • existing upgrade   → backend updates the sub inline (no URL) → refresh
  const runCheckout = async () => {
    if (!chosen) return;
    setCheckingOut(true);
    try {
      const payload = {
        priceId: chosen.priceId,
        customerEmail: user?.email,
        metadata: {
          userId: user?.id,
          plan_id: chosen.planId,
          billingCycle: chosen.interval,
          trigger_feature: source,
        },
      };
      const result = await dispatch(subscriptionPayment(payload)).unwrap();
      const checkoutUrl =
        result?.subscriptionPaymentItems?.url ||
        result?.url ||
        result?.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      await dispatch(refreshUserProfile());
      toast.success("Your plan has been updated.");
      onClose();
    } catch (error) {
      console.error("Upgrade checkout failed:", error);
      toast.error("Could not complete the upgrade. Please try again.");
    } finally {
      setCheckingOut(false);
    }
  };

  const handleContinue = () => {
    if (!chosen || checkingOut) return;

    // Pro Yearly + trial-eligible → the trial is NOT a normal subscription
    // checkout. Route to the dedicated /trial page (which calls start-trial to
    // create the Pro-Yearly-with-3-day-trial Stripe session), exactly like the
    // subscription page does. Going through subscriptionPayment here would bill
    // the yearly plan immediately instead of starting the free trial.
    if (chosenIsTrial) {
      onClose();
      navigate("/trial", {
        state: { billingCycle: "year", source },
      });
      return;
    }

    // Existing paid subscriber upgrading → show the proration confirmation
    // summary first (fetch the preview), same as the subscription page.
    if (hasExistingSubscription) {
      setView("confirm");
      dispatch(previewUpgrade({ userId: user?.id, newPriceId: chosen.priceId }));
      return;
    }

    // Free / no existing subscription → go straight to Stripe Checkout.
    runCheckout();
  };

  // Back out of the confirm step to the plan picker.
  const backToSelect = () => {
    if (checkingOut) return;
    dispatch(clearPreview());
    setView("select");
  };

  const formatCycle = (cycle) => (cycle === "year" ? "Yearly" : "Monthly");

  if (!show) return null;

  return createPortal(
    <div
      className="mmp-upgrade-modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 99998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--mmp-surface, #fff)",
          borderRadius: 16,
          width: "100%",
          maxWidth: 440,
          maxHeight: "86vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.28)",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "18px 20px 8px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "var(--mmp-text-primary, #0E2A47)",
                letterSpacing: "-0.01em",
              }}
            >
              {view === "confirm" ? "Confirm upgrade" : "Upgrade your plan"}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "var(--mmp-text-secondary, #6B7280)",
                marginTop: 3,
              }}
            >
              Current plan:{" "}
              <strong style={{ color: "var(--mmp-text-primary, #364152)" }}>
                {currentLabel}
              </strong>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "none",
              background: "var(--mmp-hover-bg, #F4F6F8)",
              color: "var(--mmp-text-secondary, #364152)",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              flexShrink: 0,
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "6px 20px 20px" }}>
          {view === "confirm" ? (
            <div>
              {isPreviewLoading && (
                <div
                  style={{
                    padding: "30px 8px",
                    textAlign: "center",
                    color: "var(--mmp-text-secondary, #6B7280)",
                    fontSize: 14,
                  }}
                >
                  Calculating charges…
                </div>
              )}

              {previewError && !isPreviewLoading && (
                <div
                  style={{
                    padding: "18px",
                    textAlign: "center",
                    color: "#DC2626",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {previewError}
                </div>
              )}

              {previewData && !isPreviewLoading && (
                <div style={{ margin: "10px 0 4px" }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: "var(--mmp-text-primary, #0E2A47)",
                      marginBottom: 10,
                    }}
                  >
                    Upgrade summary
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      fontSize: 13.5,
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ color: "var(--mmp-text-secondary, #6B7280)" }}>
                      Current plan
                    </span>
                    <strong
                      style={{ color: "var(--mmp-text-primary, #0E2A47)" }}
                    >
                      {previewData.current_plan ||
                        `${currentName}${
                          currentInterval
                            ? ` (${formatCycle(currentInterval)})`
                            : ""
                        }`}
                    </strong>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      fontSize: 13.5,
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ color: "var(--mmp-text-secondary, #6B7280)" }}>
                      New plan
                    </span>
                    <strong
                      style={{ color: "var(--mmp-text-primary, #0E2A47)" }}
                    >
                      {chosen?.name} ({formatCycle(chosen?.interval)})
                    </strong>
                  </div>

                  {previewData.proration_credit > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        fontSize: 13.5,
                        marginBottom: 8,
                        color: "#16A34A",
                      }}
                    >
                      <span>Credit from current plan</span>
                      <strong>
                        -${Number(previewData.proration_credit).toFixed(2)}
                      </strong>
                    </div>
                  )}

                  <div
                    style={{
                      height: 1,
                      background: "var(--mmp-border, #E6E8EB)",
                      margin: "12px 0",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <strong
                      style={{
                        fontSize: 14,
                        color: "var(--mmp-text-primary, #0E2A47)",
                      }}
                    >
                      Amount charged today
                    </strong>
                    <strong
                      style={{
                        fontSize: 18,
                        color: "#0395D3",
                      }}
                    >
                      ${Number(previewData.amount_due || 0).toFixed(2)}
                    </strong>
                  </div>

                  {previewData.billing_cycle_reset && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "10px 12px",
                        borderRadius: 10,
                        background: "var(--mmp-accent-bg, #E8F4FB)",
                        color: "var(--mmp-text-secondary, #364152)",
                        fontSize: 12,
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Note:</strong> Your billing cycle resets to today.
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: 12,
                      fontSize: 11.5,
                      color: "var(--mmp-text-secondary, #9AA3AF)",
                      lineHeight: 1.4,
                    }}
                  >
                    Payment is processed immediately using your saved payment
                    method.
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button
                  type="button"
                  onClick={backToSelect}
                  disabled={checkingOut}
                  style={{
                    flex: 1,
                    height: 50,
                    borderRadius: 12,
                    border: "1px solid var(--mmp-border, #D5D9DE)",
                    background: "var(--mmp-surface, #fff)",
                    color: "var(--mmp-text-primary, #0E2A47)",
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: checkingOut ? "default" : "pointer",
                  }}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={runCheckout}
                  disabled={
                    isPreviewLoading ||
                    !!previewError ||
                    !previewData ||
                    checkingOut
                  }
                  style={{
                    flex: 2,
                    height: 50,
                    borderRadius: 12,
                    border: "none",
                    background:
                      isPreviewLoading || previewError || !previewData || checkingOut
                        ? "var(--mmp-hover-bg, #EEF1F4)"
                        : "#0395D3",
                    color:
                      isPreviewLoading || previewError || !previewData || checkingOut
                        ? "var(--mmp-text-secondary, #9AA3AF)"
                        : "#fff",
                    fontSize: 15,
                    fontWeight: 800,
                    cursor:
                      isPreviewLoading || previewError || !previewData || checkingOut
                        ? "default"
                        : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {checkingOut ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" />
                      Processing…
                    </>
                  ) : (
                    "Confirm & Pay"
                  )}
                </button>
              </div>
            </div>
          ) : plansLoading ? (
            <div
              style={{
                padding: "34px 8px",
                textAlign: "center",
                color: "var(--mmp-text-secondary, #6B7280)",
                fontSize: 14,
              }}
            >
              Loading plans…
            </div>
          ) : isUnmanagedSource ? (
            <div style={{ textAlign: "center", padding: "18px 8px 6px" }}>
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  background: "var(--mmp-hover-bg, #F4F6F8)",
                  color: "var(--mmp-text-secondary, #6B7280)",
                  display: "grid",
                  placeItems: "center",
                  margin: "0 auto 14px",
                }}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "var(--mmp-text-primary, #0E2A47)",
                  marginBottom: 6,
                }}
              >
                Manage your subscription elsewhere
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--mmp-text-secondary, #6B7280)",
                  lineHeight: 1.5,
                  maxWidth: 320,
                  margin: "0 auto",
                }}
              >
                {sourceNote}
              </div>
              <button
                type="button"
                onClick={onClose}
                style={{
                  marginTop: 20,
                  width: "100%",
                  height: 48,
                  borderRadius: 12,
                  border: "none",
                  background: "#0395D3",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Got it
              </button>
            </div>
          ) : atTopPlan ? (
            <div style={{ textAlign: "center", padding: "18px 8px 6px" }}>
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  background: "var(--mmp-accent-bg, #E8F4FB)",
                  color: "#0395D3",
                  display: "grid",
                  placeItems: "center",
                  margin: "0 auto 14px",
                }}
              >
                <svg width="26" height="26" viewBox="0 0 24 24">
                  <path
                    d="M4 5.5 L7 3 L17 3 L20 5.5 L20 12 C20 16 16.5 18.8 12 21.2 C7.5 18.8 4 16 4 12 Z"
                    fill="#C8941C"
                  />
                  <rect x="10.9" y="6.4" width="2.2" height="6.8" rx="0.6" fill="#FFF" />
                  <rect x="8.6" y="8.7" width="6.8" height="2.2" rx="0.6" fill="#FFF" />
                </svg>
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "var(--mmp-text-primary, #0E2A47)",
                  marginBottom: 6,
                }}
              >
                You're on the top plan
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--mmp-text-secondary, #6B7280)",
                  lineHeight: 1.5,
                  maxWidth: 300,
                  margin: "0 auto",
                }}
              >
                You already have Pro + Newsletter — every community and app
                feature is unlocked. There's nothing higher to upgrade to.
              </div>
              <button
                type="button"
                onClick={onClose}
                style={{
                  marginTop: 20,
                  width: "100%",
                  height: 48,
                  borderRadius: 12,
                  border: "none",
                  background: "#0395D3",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Got it
              </button>
            </div>
          ) : (
            <>
              {/* Billing-cycle toggle — animated sliding pill */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  padding: 3,
                  background: "var(--mmp-hover-bg, #F4F6F8)",
                  borderRadius: 12,
                  margin: "10px 0 16px",
                }}
              >
                {/* Sliding indicator sits behind the buttons */}
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 3,
                    left: 0,
                    bottom: 3,
                    borderRadius: 9,
                    background: "var(--mmp-surface, #fff)",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
                    pointerEvents: "none",
                    ...sliderStyle,
                  }}
                />
                {[
                  { key: "month", label: "Monthly", ref: monthBtnRef },
                  { key: "year", label: "Yearly", ref: yearBtnRef },
                ].map((c) => {
                  const active = billingCycle === c.key;
                  return (
                    <button
                      key={c.key}
                      ref={c.ref}
                      type="button"
                      onClick={() => setBillingCycle(c.key)}
                      style={{
                        position: "relative",
                        zIndex: 1,
                        flex: 1,
                        height: 38,
                        border: "none",
                        borderRadius: 9,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        fontSize: 13.5,
                        fontWeight: 700,
                        background: "transparent",
                        color: active
                          ? "var(--mmp-text-primary, #0E2A47)"
                          : "var(--mmp-text-secondary, #6B7280)",
                        transition: "color 200ms ease",
                      }}
                    >
                      {c.label}
                      {c.key === "year" && bestYearlyDiscount > 0 && (
                        <span
                          style={{
                            padding: "1px 6px",
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 800,
                            background: "#E7F6EC",
                            color: "#16A34A",
                          }}
                        >
                          SAVE {bestYearlyDiscount}%
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Plan tier cards for the selected cycle */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginBottom: 18,
                }}
              >
                {tiers.map((tier) => {
                  const o = optionFor(tier);
                  if (!o) return null;
                  const sel = tier.name === selectedName;
                  const recommended = tier.rank === Math.max(...tiers.map((t) => t.rank));
                  return (
                    <button
                      key={tier.name}
                      type="button"
                      onClick={() => setSelectedName(tier.name)}
                      style={{
                        position: "relative",
                        textAlign: "left",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        padding: "16px 16px",
                        borderRadius: 16,
                        cursor: "pointer",
                        background: sel
                          ? "var(--mmp-accent-bg, #E8F4FB)"
                          : "var(--mmp-surface, #fff)",
                        border: `2px solid ${
                          sel ? "#0395D3" : "var(--mmp-border, #E6E8EB)"
                        }`,
                        boxShadow: sel
                          ? "0 6px 18px rgba(3,149,211,0.14)"
                          : "none",
                        transition: "all 140ms",
                      }}
                    >
                      {recommended && (
                        <span
                          style={{
                            position: "absolute",
                            top: -10,
                            right: 14,
                            padding: "2px 10px",
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: "0.03em",
                            background: "#0395D3",
                            color: "#fff",
                          }}
                        >
                          BEST VALUE
                        </span>
                      )}
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          flexShrink: 0,
                          marginTop: 2,
                          border: `2px solid ${sel ? "#0395D3" : "var(--mmp-border, #D5D9DE)"}`,
                          background: sel ? "#0395D3" : "transparent",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        {sel && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        )}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 15.5,
                              fontWeight: 800,
                              color: "var(--mmp-text-primary, #0E2A47)",
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {tier.name}
                          </span>
                          {trialAvailable && isProYearlyOption(o) && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "2px 8px",
                                borderRadius: 999,
                                fontSize: 10.5,
                                fontWeight: 800,
                                letterSpacing: "0.01em",
                                background: "#E7F6EC",
                                color: "#16A34A",
                              }}
                            >
                              <svg
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M13 2 3 14h7l-1 8 10-12h-7z" />
                              </svg>
                              {PRO_TRIAL_DAYS}-day free trial
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontSize: 12.5,
                            color: "var(--mmp-text-secondary, #6B7280)",
                            marginTop: 3,
                            lineHeight: 1.4,
                          }}
                        >
                          {TIER_BLURB[tier.name] || "Unlock premium features."}
                        </span>
                      </span>
                      <span
                        style={{
                          textAlign: "right",
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        <span
                          style={{
                            display: "block",
                            fontSize: 19,
                            fontWeight: 800,
                            color: "var(--mmp-text-primary, #0E2A47)",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {o.price != null && o.price !== "" ? `$${o.price}` : "—"}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "var(--mmp-text-secondary, #9AA3AF)",
                          }}
                        >
                          {billingCycle === "year" ? "/year" : "/month"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                disabled={!chosen || checkingOut}
                onClick={handleContinue}
                style={{
                  width: "100%",
                  height: 52,
                  borderRadius: 12,
                  border: "none",
                  background: chosen ? "#0395D3" : "var(--mmp-hover-bg, #EEF1F4)",
                  color: chosen ? "#fff" : "var(--mmp-text-secondary, #9AA3AF)",
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: chosen && !checkingOut ? "pointer" : "default",
                  letterSpacing: "-0.005em",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {checkingOut ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" />
                    Redirecting to checkout…
                  </>
                ) : chosenIsTrial ? (
                  `Start ${PRO_TRIAL_DAYS}-day free trial`
                ) : chosen ? (
                  `Continue with ${chosen.name}`
                ) : (
                  "Select a plan"
                )}
              </button>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--mmp-text-secondary, #9AA3AF)",
                  textAlign: "center",
                  marginTop: 10,
                  lineHeight: 1.4,
                }}
              >
                {chosenIsTrial
                  ? `No charge today. $${chosen.price}/year after your ${PRO_TRIAL_DAYS}-day trial. Cancel anytime.`
                  : "Cancel anytime. Renews automatically until cancelled."}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export { PlanShield, UpgradePlanModal, PLAN_SHIELD_COLORS };
export default UpgradePlanModal;
